/**
 * Tasks page data layer — the real personal-task API owned by services/calendar
 * (#1307). Same resource the email module's "My Tasks" pane uses, so every
 * mutation here also invalidates that pane's cache to keep both in sync.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_CALENDAR_PREFIX, SuiteApiClient } from "@weco/suite-client";
import { fromApi, toApiPatch } from "./model";
import { enqueue, isNetworkError } from "./lib/offlineQueue";
import type { ApiTask, TaskDraft, TaskItem, TaskPatch } from "./model";

const client = new SuiteApiClient(API_CALENDAR_PREFIX);

export const TASKS_QUERY_KEY = ["tasks", "page", "all"] as const;
/** The email module's "My Tasks" pane cache (modules/email/hooks/useTasks.ts). */
const EMAIL_TASKS_KEY = ["email", "tasks"] as const;

interface Page {
  results: ApiTask[];
  next?: string | null;
}

/** Hard stop so a misbehaving `next` link can never loop forever (20 rows/page). */
const MAX_PAGES = 50;

export async function fetchAllTasks(): Promise<TaskItem[]> {
  const rows: ApiTask[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const data = await client.get<Page | ApiTask[]>(`/tasks/?page=${page}`);
    if (Array.isArray(data)) {
      rows.push(...data);
      break;
    }
    rows.push(...(data.results ?? []));
    if (!data.next) break;
  }
  return rows.map(fromApi);
}

export const tasksApi = {
  create: async (draft: TaskDraft): Promise<TaskItem> =>
    fromApi(await client.post<ApiTask>("/tasks/", toApiPatch(draft))),
  update: async (id: string, patch: TaskPatch): Promise<TaskItem> =>
    fromApi(await client.patch<ApiTask>(`/tasks/${id}/`, toApiPatch(patch))),
  remove: async (id: string): Promise<void> => {
    await client.delete<void>(`/tasks/${id}/`);
  },
};

export function useTasksQuery() {
  return useQuery({ queryKey: TASKS_QUERY_KEY, queryFn: fetchAllTasks });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
    void qc.invalidateQueries({ queryKey: EMAIL_TASKS_KEY });
  };
}

export function useCreateTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (draft: TaskDraft) => tasksApi.create(draft),
    onSuccess: (created) => {
      qc.setQueryData<TaskItem[]>(TASKS_QUERY_KEY, (prev) => (prev ? [created, ...prev] : [created]));
      invalidate();
    },
  });
}

/** Optimistic: the row changes immediately and rolls back if the PATCH fails.
 *  Offline, the edit is queued and sent when the connection returns (US 3.4). */
export function useUpdateTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: TaskPatch }) => {
      try {
        return await tasksApi.update(id, patch);
      } catch (err) {
        if (!isNetworkError(err)) throw err;
        enqueue({ kind: "personal-task", taskId: id, patch });
        return null;
      }
    },
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: TASKS_QUERY_KEY });
      const prev = qc.getQueryData<TaskItem[]>(TASKS_QUERY_KEY);
      qc.setQueryData<TaskItem[]>(TASKS_QUERY_KEY, (list) =>
        list?.map((t) => (t.id === id ? { ...t, ...patch } : t)),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(TASKS_QUERY_KEY, ctx.prev);
    },
    onSettled: invalidate,
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => tasksApi.remove(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: TASKS_QUERY_KEY });
      const prev = qc.getQueryData<TaskItem[]>(TASKS_QUERY_KEY);
      qc.setQueryData<TaskItem[]>(TASKS_QUERY_KEY, (list) => list?.filter((t) => t.id !== id));
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(TASKS_QUERY_KEY, ctx.prev);
    },
    onSettled: invalidate,
  });
}
